// src/pages/UserProfile.jsx
import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetLoggedInUserQuery,
  useGetTenantInfoQuery,
  useLogoutUserMutation,
} from "../toolkitServices/userApi";
import { useRequestEmployeeCommissionMutation } from "../toolkitServices/employeeApi";
import toast from "react-hot-toast";

/* ---------------- animations ---------------- */
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.35, ease: "easeOut" },
  },
});

const modalOverlay = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
};

const modalPanel = {
  hidden: { opacity: 0, y: 14, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.18 } },
  exit: { opacity: 0, y: 10, scale: 0.98, transition: { duration: 0.12 } },
};

/* ---------------- helpers ---------------- */
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const shortInr = (v, decimals = 2) => {
  const n = Number(v || 0);
  if (!isFinite(n)) return "₹ 0.00";
  return `₹ ${n.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

const safeStr = (v, dash = "—") => {
  const s = String(v ?? "").trim();
  return s ? s : dash;
};

const maskMid = (value = "", keepStart = 2, keepEnd = 2) => {
  const s = String(value ?? "").trim();
  if (!s) return "—";
  if (s.length <= keepStart + keepEnd) return s;
  const mid = "•".repeat(Math.max(3, s.length - keepStart - keepEnd));
  return `${s.slice(0, keepStart)}${mid}${s.slice(-keepEnd)}`;
};

const initialsFrom = (name = "") => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "U";
  const a = (parts[0]?.[0] || "U").toUpperCase();
  const b = (parts[1]?.[0] || "").toUpperCase();
  return `${a}${b}`.slice(0, 2);
};

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

/* ---------------- UI atoms ---------------- */
const Card = ({ className = "", children }) => (
  <div
    className={[
      "rounded-2xl bg-white border border-slate-200 shadow-sm",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

const Sk = ({ className = "", style }) => (
  <div
    className={[
      "animate-pulse rounded-xl bg-slate-200/70 border border-slate-200",
      className,
    ].join(" ")}
    style={style}
  />
);

const SectionTitle = ({ title, desc }) => (
  <div>
    <p className="text-[12px] font-semibold text-slate-900">{title}</p>
    {desc ? <p className="text-[11px] text-slate-500">{desc}</p> : null}
  </div>
);

const Chip = ({ children, tone = "violet", className = "" }) => {
  const toneCls =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : tone === "amber"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : tone === "slate"
          ? "bg-slate-50 text-slate-700 border-slate-200"
          : "bg-violet-50 text-violet-700 border-violet-200";
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold border",
        toneCls,
        className,
      ].join(" ")}
    >
      {children}
    </span>
  );
};

const Stat = ({ label, value, tone = "slate" }) => {
  const cls =
    tone === "emerald"
      ? "bg-emerald-50 border-emerald-200"
      : tone === "amber"
        ? "bg-amber-50 border-amber-200"
        : tone === "violet"
          ? "bg-violet-50 border-violet-200"
          : "bg-slate-50 border-slate-200";
  return (
    <div className={`rounded-xl px-3 py-2 border ${cls}`}>
      <div className="text-[10px] text-slate-600">{label}</div>
      <div className="mt-0.5 text-[12px] font-extrabold text-slate-900">
        {value}
      </div>
    </div>
  );
};

/* ---------------- Permission helpers ---------------- */
const getPermScore = (p) => {
  const perms = p?.permissions || {};
  const keys = ["view", "create", "edit", "delete", "approve"];
  return keys.reduce((acc, k) => acc + (perms?.[k] ? 1 : 0), 0);
};

const permTone = (score) => {
  if (score >= 4) return "emerald";
  if (score >= 2) return "violet";
  if (score >= 1) return "amber";
  return "slate";
};

const PermissionChip = ({ moduleKey, score }) => {
  const tone = permTone(score);
  const label =
    score >= 4 ? "Full" : score >= 2 ? "Limited" : score >= 1 ? "View" : "No";

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white px-3 py-2 hover:bg-slate-50 transition"
      title={`${moduleKey} • ${label}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[11px] font-semibold text-slate-900">
            {String(moduleKey || "module")}
          </p>
          <p className="text-[10px] text-slate-500">{label}</p>
        </div>
        <Chip tone={tone} className="shrink-0">
          {score}/5
        </Chip>
      </div>
    </div>
  );
};

export default function UserProfile() {
  const auth = useSelector((s) => s.auth);
  const fallbackUser = auth?.user;
  const role = auth?.user?.role;

  // ✅ token guard (adjust key name if different)
  const token = auth?.token || auth?.accessToken || auth?.jwt || "";

  const {
    data: meRes,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetLoggedInUserQuery("me", {
    skip: !token,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: tenantRes,
    isLoading: tenantLoading,
    isFetching: tenantFetching,
    isError: tenantError,
  } = useGetTenantInfoQuery();

  const [logoutUser, { isLoading: logoutLoading }] = useLogoutUserMutation();

  const me = useMemo(() => {
    const raw = meRes?.data ?? meRes ?? fallbackUser ?? {};
    return raw || {};
  }, [meRes, fallbackUser]);

  const employee = me?.employeeDetails || {};
  const permissions = Array.isArray(me?.permissions) ? me.permissions : [];

  const displayName = useMemo(() => {
    const fn = safeStr(employee?.firstName, "");
    const ln = safeStr(employee?.lastName, "");
    const joined = `${fn} ${ln}`.trim();
    return joined || safeStr(me?.userName, "User");
  }, [employee, me]);

  const roleName = useMemo(() => {
    return (
      me?.employeeRole?.roleName ||
      employee?.role?.roleName ||
      safeStr(me?.role, "Role")
    );
  }, [me, employee]);

  const isActive = Boolean(employee?.isActive ?? true);
  const createdAt = employee?.createdAt || me?.createdAt;
  const updatedAt = employee?.updatedAt || me?.updatedAt;

  const commission = Number(employee?.employeeCommission || 0);
  const canRedeem = commission > 0;
  console.log(tenantRes);
  const tenantInfo = tenantRes?.data?.data || tenantRes?.data || {};
  const subscription = tenantInfo?.subscription || {};
  const smsInfo = tenantInfo?.sms || {};

  const currentPlan = safeStr(subscription?.plan, "—");
  const planPrice = Number(subscription?.price || 0);
  const planStartDate = subscription?.startDate || null;
  const planExpiry = subscription?.expiryDate || null;
  const planExpired = planExpiry
    ? new Date(planExpiry).getTime() < Date.now()
    : false;

  const smsBalance = Number(smsInfo?.balance || 0);
  const smsUsed = Number(smsInfo?.totalUsed || 0);
  const smsEnabled = smsInfo?.isEnabled ?? false;
  const smsSenderId = safeStr(smsInfo?.senderId, "—");

  const permStats = useMemo(() => {
    const rows = permissions.map((p) => ({
      moduleKey: p?.moduleKey,
      score: getPermScore(p),
      perms: p?.permissions || {},
    }));
    const accessible = rows.filter((r) => r.score > 0);
    const full = rows.filter((r) => r.score >= 4);
    const viewOnly = rows.filter((r) => r.score === 1 && r.perms?.view);
    const restricted = rows.filter((r) => r.score === 0);
    return {
      rows,
      totalModules: rows.length,
      accessible: accessible.length,
      full: full.length,
      viewOnly: viewOnly.length,
      restricted: restricted.length,
    };
  }, [permissions]);

  const isBusy = isLoading || isFetching || tenantLoading || tenantFetching;
  const [requestforCommission, { isLoading: isRequesting }] =
    useRequestEmployeeCommissionMutation();
  /* -------- Redeem modal (UI only) -------- */
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemAmt, setRedeemAmt] = useState("");
  const [redeemNote, setRedeemNote] = useState("");
  const [redeemErr, setRedeemErr] = useState("");

  const closeRedeem = () => {
    setRedeemOpen(false);
    setRedeemAmt("");
    setRedeemNote("");
    setRedeemErr("");
  };

  // const submitRedeem = () => {
  //   setRedeemErr("");
  //   const amt = Number(redeemAmt || 0);
  //   if (!amt || amt <= 0) return setRedeemErr("Enter redeem amount.");
  //   if (amt > commission) return setRedeemErr("Amount exceeds available commission.");
  //   // ✅ connect your redeem API here
  //   closeRedeem();
  // };
  const submitRedeem = async () => {
    setRedeemErr("");
    const employeeId = employee?._id;
    const amt = Number(redeemAmt || 0);
    if (!employeeId) {
      return setRedeemErr("You can't send reedem request.");
    }
    if (!amt || amt <= 0) return setRedeemErr("Enter redeem amount.");

    if (amt > commission)
      return setRedeemErr("Amount exceeds available commission.");

    try {
      await requestforCommission({
        id: employeeId,
        amount: amt,
      }).unwrap();
      toast.success("Commission withdrawl reminder sent successfully", {
        position: "top-center",
      });

      closeRedeem();
    } catch (err) {
      setRedeemErr(
        err?.response?.data?.message || "Failed to send redeem request",
      );
    }
  };

  /* -------- ESC + body scroll lock for modal -------- */
  useEffect(() => {
    if (!redeemOpen) return;
    const onKey = (e) => e.key === "Escape" && closeRedeem();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [redeemOpen]);

  // Permissions expand
  const [permOpen, setPermOpen] = useState(false);

  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const HeaderSkeleton = () => (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <Sk className="h-14 w-14 rounded-2xl" />
        <div className="flex-1 space-y-2">
          <Sk className="h-4 w-48" />
          <Sk className="h-3 w-64" />
          <div className="flex gap-2">
            <Sk className="h-7 w-24 rounded-full" />
            <Sk className="h-7 w-28 rounded-full" />
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="relative bg-white shadow-lg rounded-xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)] overflow-hidden">
      {/* Top header bar (SalesList vibe) */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#9481ce] text-white">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-base sm:text-lg font-semibold tracking-tight truncate">
            User Profile
          </h2>
          {isBusy && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/15">
              <Spinner size={10} /> syncing…
            </span>
          )}
          <span className="hidden sm:inline text-[11px] text-violet-100/90">
            📅 {formattedDate}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-slate-50/60 p-3 sm:p-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp(0)}
          className="mx-auto max-w-7xl pb-4"
        >
          {/* Error */}
          {isError ? (
            <Card className="p-4 mb-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Could not load profile
                  </p>
                  <p className="mt-1 text-[12px] text-slate-600">
                    {String(
                      error?.data?.message || error?.error || "Unknown error",
                    )}
                  </p>
                </div>
                <button
                  onClick={() => refetch()}
                  className="rounded-full bg-rose-600 px-4 py-2 text-[12px] font-semibold text-white shadow hover:bg-rose-700 disabled:opacity-60"
                  type="button"
                  disabled={!token}
                >
                  Retry
                </button>
              </div>
            </Card>
          ) : null}

          {/* Header */}
          <motion.div variants={fadeUp(0.05)} className="mb-4">
            {isLoading && !meRes ? (
              <HeaderSkeleton />
            ) : (
              <Card className="p-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    {role === "appowner" ? (
                      <Link
                        to="/admin/tenant-list"
                        className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-900 text-amber-300 font-extrabold shadow-sm"
                        title="Profile"
                      >
                        {initialsFrom(displayName)}
                      </Link>
                    ) : (
                      <div
                        className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-900 text-amber-300 font-extrabold shadow-sm"
                        title="Profile"
                      >
                        {initialsFrom(displayName)}
                      </div>
                    )}

                    <div className="min-w-0">
                      <h1 className="text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
                        {displayName}
                      </h1>
                      <p className="mt-0.5 text-[12px] text-slate-600 truncate">
                        {safeStr(
                          me?.email,
                          safeStr(employee?.officialEmail, "—"),
                        )}{" "}
                        • {safeStr(me?.phone, employee?.contact)}
                      </p>

                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Chip tone="violet">🧑‍💼 {roleName}</Chip>
                        <Chip tone={isActive ? "emerald" : "amber"}>
                          {isActive ? "✅ Active" : "⛔ Inactive"}
                        </Chip>
                        <Chip tone="slate">
                          🧾 Last sync: {fmtDateTime(updatedAt)}
                        </Chip>
                        <Chip tone="violet">📦 Plan: {currentPlan}</Chip>
                        <Chip tone={smsEnabled ? "emerald" : "amber"}>
                          ✉️ SMS:{" "}
                          {smsEnabled ? `${smsBalance} left` : "Disabled"}
                        </Chip>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:min-w-[520px]">
                    <Stat
                      label="Employee Code"
                      value={safeStr(employee?.employeeCode)}
                    />
                    <Stat
                      label="Branch"
                      value={employee?.branch?.branchName || "—"}
                    />
                    <Stat label="Plan" value={currentPlan} tone="violet" />
                    <Stat
                      label="SMS Left"
                      value={smsBalance}
                      tone={smsEnabled ? "emerald" : "amber"}
                    />
                  </div>
                </div>
              </Card>
            )}
          </motion.div>

          {/* Main grid */}
          <motion.div
            variants={fadeUp(0.1)}
            className="grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr,0.9fr]"
          >
            {/* LEFT */}
            <div className="flex flex-col gap-4">
              {/* Commission Wallet (clean) */}
              <Card className="overflow-hidden">
                <div className="px-4 py-3 bg-[#9481ce] text-white">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold tracking-tight">
                        Commission Wallet
                      </div>
                      <div className="text-[11px] text-violet-100/95">
                        Redeem available commission (policy based)
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-[10px] text-violet-100/90">
                        Available
                      </div>
                      <div className="text-lg font-extrabold">
                        {shortInr(commission)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700">
                      Created:{" "}
                      <span className="font-semibold">
                        {fmtDateTime(createdAt)}
                      </span>
                      <span className="mx-2 text-slate-300">•</span>
                      Updated:{" "}
                      <span className="font-semibold">
                        {fmtDateTime(updatedAt)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setRedeemOpen(true)}
                        disabled={!canRedeem}
                        className={[
                          "rounded-full px-4 py-2 text-[11px] font-semibold shadow-sm transition",
                          canRedeem
                            ? "bg-slate-900 text-amber-300 hover:bg-slate-800"
                            : "bg-slate-200 text-slate-500 cursor-not-allowed",
                        ].join(" ")}
                        type="button"
                        title={
                          canRedeem
                            ? "Redeem commission"
                            : "No commission to redeem"
                        }
                      >
                        Redeem
                      </button>

                      <Link
                        to="/sale-report"
                        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                        title="View sales report"
                      >
                        View performance
                      </Link>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <Stat
                      label="Wallet Status"
                      value={canRedeem ? "Ready" : "No balance"}
                      tone={canRedeem ? "emerald" : "slate"}
                    />
                    <Stat
                      label="Commission"
                      value={shortInr(commission)}
                      tone="amber"
                    />
                    <Stat
                      label="Employee Code"
                      value={safeStr(employee?.employeeCode)}
                    />
                    <Stat label="Role" value={roleName} tone="violet" />
                  </div>
                </div>
              </Card>

              {/* Permissions overview */}
              <Card className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <SectionTitle
                    title="Permissions"
                    desc="Quick overview of your module access."
                  />
                  <button
                    type="button"
                    onClick={() => setPermOpen((s) => !s)}
                    className="text-[11px] font-semibold text-[#6b59c6] hover:underline"
                  >
                    {permOpen ? "Show less" : "Show all"}
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Stat label="Total modules" value={permStats.totalModules} />
                  <Stat
                    label="Accessible"
                    value={permStats.accessible}
                    tone="emerald"
                  />
                  <Stat
                    label="Full access"
                    value={permStats.full}
                    tone="violet"
                  />
                  <Stat
                    label="Restricted"
                    value={permStats.restricted}
                    tone="amber"
                  />
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(permOpen ? permStats.rows : permStats.rows.slice(0, 8)).map(
                    (r, idx) => (
                      <PermissionChip
                        key={`${r.moduleKey || "m"}-${idx}`}
                        moduleKey={r.moduleKey}
                        score={r.score}
                      />
                    ),
                  )}

                  {!permOpen && permStats.rows.length > 8 ? (
                    <div className="sm:col-span-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
                      +{permStats.rows.length - 8} more modules — click{" "}
                      <span className="font-semibold">Show all</span>
                    </div>
                  ) : null}
                </div>
              </Card>
            </div>

            {/* RIGHT */}
            <div className="flex flex-col gap-4">
              {/* Plan & SMS details */}
              <Card className="p-4">
                <SectionTitle
                  title="Plan & SMS Status"
                  desc="Subscription and SMS wallet details."
                />

                <div className="mt-3 grid grid-cols-1 gap-3">
                  {/* Plan */}
                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold text-slate-900">
                        Current Plan
                      </p>
                      <Chip tone={planExpired ? "amber" : "violet"}>
                        {planExpired ? "Expired" : "Active"}
                      </Chip>
                    </div>

                    <div className="mt-2 grid grid-cols-1 gap-2 text-[12px] text-slate-700">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Plan Type</span>
                        <span className="font-semibold uppercase">
                          {currentPlan}
                        </span>
                      </div>

                      {role === "appowner" && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-slate-500">Price</span>
                          <span className="font-semibold">
                            {shortInr(planPrice)}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Start Date</span>
                        <span className="font-semibold">
                          {planStartDate ? fmtDateTime(planStartDate) : "—"}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Expiry Date</span>
                        <span className="font-semibold">
                          {planExpiry ? fmtDateTime(planExpiry) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* SMS */}
                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold text-slate-900">
                        SMS Wallet
                      </p>
                      <Chip tone={smsEnabled ? "emerald" : "amber"}>
                        {smsEnabled ? "Enabled" : "Disabled"}
                      </Chip>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                      <Stat
                        label="Remaining SMS"
                        value={smsBalance}
                        tone="emerald"
                      />
                      <Stat label="Used SMS" value={smsUsed} tone="amber" />
                      <Stat
                        label="Sender ID"
                        value={smsSenderId}
                        tone="violet"
                      />
                    </div>
                  </div>
                </div>
              </Card>
              {/* Profile details */}
              <Card className="p-4">
                <SectionTitle
                  title="Profile details"
                  desc="Personal + work info snapshot."
                />

                <div className="mt-3 grid grid-cols-1 gap-3">
                  {/* Contact */}
                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-[11px] font-semibold text-slate-900">
                      Contact
                    </p>
                    <div className="mt-2 grid grid-cols-1 gap-2 text-[12px] text-slate-700">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Email</span>
                        <span className="font-semibold truncate max-w-[70%]">
                          {safeStr(me?.email, employee?.officialEmail)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">
                          Official Email / Gmail
                        </span>
                        <span className="font-semibold truncate max-w-[70%]">
                          {employee?.email || "-"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Phone</span>
                        <span className="font-semibold">
                          {safeStr(me?.phone, employee?.contact)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Work */}
                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-[11px] font-semibold text-slate-900">
                      Work
                    </p>
                    <div className="mt-2 grid grid-cols-1 gap-2 text-[12px] text-slate-700">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Employee Code</span>
                        <span className="font-semibold">
                          {safeStr(employee?.employeeCode)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Department</span>
                        <span className="font-semibold">
                          {employee?.department?.departmentName || "—"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Branch</span>
                        <span className="font-semibold">
                          {employee?.branch?.branchName || "—"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Counter</span>
                        <span className="font-semibold">
                          {employee?.counter?.counterName || "—"}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-[10px] text-slate-600 border border-slate-200">
                      Created:{" "}
                      <span className="font-semibold">
                        {fmtDateTime(createdAt)}
                      </span>
                      <span className="mx-2 text-slate-300">•</span>
                      Updated:{" "}
                      <span className="font-semibold">
                        {fmtDateTime(updatedAt)}
                      </span>
                    </div>
                  </div>

                  {/* KYC */}
                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-[11px] font-semibold text-slate-900">
                      KYC
                    </p>
                    <div className="mt-2 grid grid-cols-1 gap-2 text-[12px] text-slate-700">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">PAN</span>
                        <span className="font-semibold">
                          {maskMid(employee?.pan || "", 2, 2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Aadhaar</span>
                        <span className="font-semibold">
                          {maskMid(employee?.adhar || "", 2, 2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Bank */}
                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-[11px] font-semibold text-slate-900">
                      Bank
                    </p>
                    <div className="mt-2 grid grid-cols-1 gap-2 text-[12px] text-slate-700">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">Bank</span>
                        <span className="font-semibold truncate max-w-[70%]">
                          {safeStr(employee?.bankName)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">A/C</span>
                        <span className="font-semibold">
                          {maskMid(employee?.accountNumber || "", 2, 3)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500">IFSC</span>
                        <span className="font-semibold">
                          {safeStr(employee?.ifscCode)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-[10px] text-amber-700 border border-amber-200">
                      Keep bank details updated for faster payouts.
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </motion.div>

          {/* Redeem Modal */}
          <AnimatePresence>
            {redeemOpen && (
              <motion.div
                variants={modalOverlay}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fixed inset-0 z-[9999] bg-black/45 flex items-center justify-center p-3 sm:p-6"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) closeRedeem();
                }}
                role="dialog"
                aria-modal="true"
              >
                <motion.div
                  variants={modalPanel}
                  className="w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden bg-white"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="relative px-4 sm:px-6 py-4 text-white bg-gradient-to-r from-[#6b59c6] via-[#7b66d6] to-[#9481ce]">
                    <div className="absolute inset-0 opacity-20">
                      <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/30 blur-2xl" />
                      <div className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full bg-white/30 blur-2xl" />
                    </div>

                    <div className="relative flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm sm:text-base font-semibold tracking-tight">
                          Redeem Commission
                        </div>
                        <div className="mt-0.5 text-[11px] sm:text-xs text-violet-100/95 leading-4">
                          Available:{" "}
                          <span className="font-semibold text-white">
                            {shortInr(commission)}
                          </span>{" "}
                          <span className="opacity-70">•</span>{" "}
                          <span className="font-semibold text-white/90">
                            ESC
                          </span>{" "}
                          to close
                        </div>
                      </div>

                      <button
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 transition"
                        onClick={closeRedeem}
                        title="Close"
                        type="button"
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="p-4 sm:p-5 space-y-3">
                    <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-700">
                      Enter redeem amount. (UI only) — connect your API in{" "}
                      <span className="font-semibold">submitRedeem()</span>.
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] font-medium text-slate-500">
                          Redeem amount (₹)
                        </span>
                        <input
                          type="number"
                          value={redeemAmt}
                          onChange={(e) => setRedeemAmt(e.target.value)}
                          className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-[#6b59c6] focus:ring-2 focus:ring-violet-100"
                          placeholder="e.g. 500"
                          min="0"
                        />
                      </label>

                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] font-medium text-slate-500">
                          Note (optional)
                        </span>
                        <textarea
                          value={redeemNote}
                          onChange={(e) => setRedeemNote(e.target.value)}
                          className="min-h-[90px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[12px] text-slate-700 outline-none focus:border-[#6b59c6] focus:ring-2 focus:ring-violet-100"
                          placeholder="Any note for admin / accounts…"
                        />
                      </label>

                      {redeemErr ? (
                        <div className="rounded-xl bg-rose-50 px-3 py-2 text-[11px] text-rose-700 border border-rose-200">
                          {redeemErr}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="px-4 sm:px-6 py-3 border-t bg-slate-50 flex items-center justify-between">
                    <button
                      onClick={closeRedeem}
                      className="px-4 py-2 rounded-full border border-slate-200 text-[11px] font-semibold text-slate-700 bg-white hover:bg-slate-100"
                      type="button"
                    >
                      Cancel
                    </button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={submitRedeem}
                      className="px-4 py-2 rounded-full bg-slate-900 text-amber-300 text-[11px] font-extrabold shadow-sm hover:bg-slate-800"
                      type="button"
                    >
                      Submit request →
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
